import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Database,
  RefreshCw,
  Download,
  Trash2,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  HardDrive,
  FileText,
  FileSpreadsheet,
  FileJson,
  Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

function formatDate(dateStr) {
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
}

export function BackupsSection() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);
  const [restoreDialog, setRestoreDialog] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importDialog, setImportDialog] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/backup/list`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al obtener backups');
      }

      const data = await response.json();
      setBackups(data.backups || []);
    } catch (error) {
      console.error('Fetch backups error:', error);
      setMessage({ type: 'error', text: 'Error al cargar backups' });
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    try {
      setCreating(true);
      setMessage(null);

      const response = await fetch(`${BACKEND_URL}/api/backup/create`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al crear backup');
      }

      const data = await response.json();
      setMessage({ type: 'success', text: `Backup creado exitosamente (${formatBytes(data.size_bytes)})` });
      fetchBackups();
    } catch (error) {
      console.error('Create backup error:', error);
      setMessage({ type: 'error', text: error.message || 'Error al crear backup' });
    } finally {
      setCreating(false);
    }
  };

  const [downloading, setDownloading] = useState(null);

  const downloadBackup = async (backupId, format = 'json') => {
    try {
      setDownloading(`${backupId}-${format}`);
      const response = await fetch(`${BACKEND_URL}/api/backup/download/${backupId}?format=${format}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al descargar backup');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `backup_${backupId.slice(0, 8)}.${format}`;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setMessage({ type: 'success', text: `Backup descargado (${format.toUpperCase()})` });
    } catch (error) {
      console.error('Download backup error:', error);
      setMessage({ type: 'error', text: 'Error al descargar backup' });
    } finally {
      setDownloading(null);
    }
  };

  const restoreBackup = async (backupId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/backup/restore`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          backup_id: backupId,
          confirm: true
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al restaurar backup');
      }

      const data = await response.json();
      setMessage({
        type: 'success',
        text: `Backup restaurado: ${data.days_restored} días, ${data.notes_restored} entradas de bitácora`
      });
      setRestoreDialog(null);
      
      // Refresh page after restoration
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Restore backup error:', error);
      setMessage({ type: 'error', text: error.message || 'Error al restaurar backup' });
      setRestoreDialog(null);
    }
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (file) setImportDialog(file);
    e.target.value = '';
  };

  const importBackup = async () => {
    const file = importDialog;
    if (!file) return;
    try {
      setImporting(true);
      setMessage(null);
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/backup/import`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al importar backup');
      }

      const data = await response.json();
      setMessage({
        type: 'success',
        text: `Backup importado: ${data.days_imported} días, ${data.notes_imported} entradas de bitácora, ${data.projects_imported} proyectos. Recargando...`
      });
      setImportDialog(null);
      setTimeout(() => {
        window.location.reload();
      }, 2500);
    } catch (error) {
      console.error('Import backup error:', error);
      setMessage({ type: 'error', text: error.message || 'Error al importar backup' });
      setImportDialog(null);
    } finally {
      setImporting(false);
    }
  };

  const deleteBackup = async (backupId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/backup/${backupId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al eliminar backup');
      }

      setMessage({ type: 'success', text: 'Backup eliminado' });
      fetchBackups();
    } catch (error) {
      console.error('Delete backup error:', error);
      setMessage({ type: 'error', text: 'Error al eliminar backup' });
    }
  };

  return (
    <>
      <Card data-testid="backups-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Backups y Restauración
          </CardTitle>
          <CardDescription>
            Protege tus datos con backups automáticos y manuales. Tu bitácora nunca se perderá.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {message && (
            <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
              {message.type === 'success' ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription>{message.text}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button
              onClick={createBackup}
              disabled={creating}
              data-testid="create-backup-btn"
            >
              {creating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <HardDrive className="mr-2 h-4 w-4" />
              )}
              Crear Backup Ahora
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleFileSelected}
              data-testid="import-backup-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              disabled={importing}
              data-testid="import-backup-btn"
            >
              {importing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Importar Backup (JSON)
            </Button>

            <Button
              onClick={fetchBackups}
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="bg-muted/50 p-3 rounded-md">
            <p className="text-sm text-muted-foreground">
              <strong>Backups automáticos:</strong> Se crea un backup automático cada día a las 3 AM.
              Tus datos están protegidos incluso si se borra la base de datos.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              <strong>Migrar datos entre entornos:</strong> Descarga un backup en formato JSON y luego usa
              "Importar Backup (JSON)" en el otro entorno (por ejemplo, en tu despliegue de producción)
              para restaurar todos tus días, tareas, bitácora, objetivos y proyectos.
            </p>
          </div>

          {/* Backups List */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Historial de Backups</h3>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : backups.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No hay backups disponibles. Crea tu primer backup.
              </p>
            ) : (
              <div className="space-y-2">
                {backups.map((backup) => (
                  <div
                    key={backup.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 border rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">
                          {backup.type === 'auto' ? '🤖 Automático' : backup.type === 'imported' ? '📥 Importado' : '👤 Manual'}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                          {backup.record_count} registros
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3 shrink-0" />
                          {formatDate(backup.created_at)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatBytes(backup.size_bytes)}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-1 flex-wrap shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadBackup(backup.id, 'csv')}
                        disabled={downloading !== null}
                        title="Descargar CSV"
                        data-testid={`download-csv-${backup.id}`}
                      >
                        {downloading === `${backup.id}-csv` ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                      </Button>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadBackup(backup.id, 'pdf')}
                        disabled={downloading !== null}
                        title="Descargar PDF"
                        data-testid={`download-pdf-${backup.id}`}
                      >
                        {downloading === `${backup.id}-pdf` ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                      </Button>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadBackup(backup.id, 'json')}
                        disabled={downloading !== null}
                        title="Descargar JSON (restauración)"
                        data-testid={`download-json-${backup.id}`}
                      >
                        {downloading === `${backup.id}-json` ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileJson className="h-4 w-4" />}
                      </Button>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRestoreDialog(backup)}
                        title="Restaurar"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget(backup)}
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={restoreDialog !== null} onOpenChange={() => setRestoreDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Restaurar desde backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción reemplazará TODOS tus datos actuales con los datos del backup.
              {restoreDialog && (
                <div className="mt-2 p-2 bg-muted rounded text-sm">
                  <p><strong>Backup:</strong> {restoreDialog.type === 'auto' ? 'Automático' : 'Manual'}</p>
                  <p><strong>Fecha:</strong> {formatDate(restoreDialog.created_at)}</p>
                  <p><strong>Registros:</strong> {restoreDialog.record_count}</p>
                </div>
              )}
              <p className="mt-2 font-semibold">Esta acción no se puede deshacer.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => restoreBackup(restoreDialog?.id)}>
              Confirmar Restauración
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Import Confirmation Dialog */}
      <AlertDialog open={importDialog !== null} onOpenChange={(open) => { if (!open) setImportDialog(null); }}>
        <AlertDialogContent data-testid="confirm-import-backup-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Importar backup desde archivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción reemplazará TODOS tus datos actuales con los datos del archivo.
              {importDialog && (
                <span className="block mt-2 p-2 bg-muted rounded text-sm">
                  <strong>Archivo:</strong> {importDialog.name} ({formatBytes(importDialog.size)})
                </span>
              )}
              <span className="block mt-2 font-semibold">Esta acción no se puede deshacer.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={importBackup} data-testid="confirm-import-backup-btn">
              Confirmar Importación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Delete Backup Confirmation Dialog */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent data-testid="confirm-delete-backup-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { deleteBackup(deleteTarget?.id); setDeleteTarget(null); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
